import React, { useState, useEffect } from 'react';
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs';
import { FavouriteIngredientStockCard, IngredientItem } from './InputGroups';
import ELayersView from 'enums/ELayersView';
import './SelectIngredients.scss';
import {
  compareWithIngredientName,
  compareWithStockConcen,
  compareWithStockPh,
  GetFavouriteStocks,
  getIngNameByIngId,
  GetIngredients,
  getIngredientsByIngredientStockId,
  getIngShortNameByIngId,
  isNullOrUndefined,
  onlyUnique,
} from 'utils/Global';
import {
  BooleanPropertyDTO,
  IngredientIngredientTypeDTO,
  IngredientStockDTO,
} from 'business/models/Models';
import SelectSearch from './SelectSearch';
import { TSelectIngredient } from 'business/models/CustomModels';
import { withTabBackButton, WithTabBackButtonStateProps } from 'components/tab';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';

interface SelectIngredientsProps extends WithTabBackButtonStateProps {
  setContentView: (contentView: ELayersView) => void;
  setSelectIngredient?: (ingredient: TSelectIngredient) => void;
  setSelectedIng: (selectedIng: string) => void;
  handleBackToPreviousScreen?: (contentView: ELayersView) => () => void;
  setSelectedStock?: (ingredientStock: IngredientStockDTO) => void;
}

export enum ingredientTypesEnum {
  All = 0,
  Buffer = 1,
  NonBuffer = 2,
  Prepared = 3,
}

function SelectIngredients(props: SelectIngredientsProps) {
  const {
    setContentView,
    setSelectedIng = null,
    setSelectIngredient,
    handleBackToPreviousScreen,
    setHandleOnTabBackPress,
    setSelectedStock,
  } = props;
  const [searchIngredient, setSearchIngredient] = useState('');
  const [searchFavStock, setSearchFavStock] = useState('');
  const [selectedIngredientTypeAll, setSelectedIngredientTypeAll] = useState(
    ingredientTypesEnum.All,
  );
  const [selectedIngredientTypeFavorite, setSelectedIngredientTypeFavorite] = useState(
    ingredientTypesEnum.All,
  );
  const [filteredIngredients, setFilteredIngredients] = useState([]);
  const [favouriteIngredientsList, setFavouriteIngredientsList] = useState([]);
  const [globalFavIngStocks, setGlobalFavIngStocks] = useState([]);
  const showIngLongName = useSystemProperty<BooleanPropertyDTO>(
    PropertyConstants.ShowIngredientLongName,
    false,
  );

  const extractIngredientsByFavStockId = (favStocks) => {
    const ingsList = [];
    for (const stock of favStocks) {
      const ing = getIngredientsByIngredientStockId(stock.ingredientStockId);
      if (ing) {
        ingsList.push(ing);
      }
    }
    return ingsList;
  };

  function changeIngTypeAll(val) {
    setSelectedIngredientTypeAll(val);
    if (!isNullOrUndefined(setSelectedIng)) {
      setSelectedIng(val);
    }
  }

  function changeIngTypeFavorite(val) {
    setSelectedIngredientTypeFavorite(val);
  }

  useEffect(() => {
    if (setSelectedIng) {
      setSelectedIng(ingredientTypesEnum[selectedIngredientTypeAll]);
    }
    const favStocks = GetFavouriteStocks();
    setGlobalFavIngStocks(favStocks);
    setFavouriteIngredientsList(extractIngredientsByFavStockId(favStocks));
  }, []);

  useEffect(() => {
    filterIngredients();
  }, [searchIngredient, selectedIngredientTypeAll]);

  useEffect(() => {
    filterFavouriteIngredients();
  }, [searchFavStock, globalFavIngStocks, selectedIngredientTypeFavorite]);

  useEffect(() => {
    if (handleBackToPreviousScreen) {
      setHandleOnTabBackPress(() =>
        handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW),
      );
    }
  }, [setHandleOnTabBackPress]);

  function isBufferIngredientType(ingIngType) {
    const ingType = GetIngredients().ingredientType.find(
      (ingType) => ingType.id === ingIngType.ingredientTypeId,
    );
    return ingType.isBuffer;
  }

  function filterIngredients() {
    let filterIng: IngredientIngredientTypeDTO[] = [];
    let tempIng3;
    const globalIngs = GetIngredients();
    switch (selectedIngredientTypeAll) {
      case ingredientTypesEnum.NonBuffer:
        filterIng = globalIngs.ingredientIngredientType.filter(
          (ingIngType) => !isBufferIngredientType(ingIngType),
        );
        tempIng3 = filterIng.sort(compareWithStockConcen);
        break;
      case ingredientTypesEnum.Buffer:
        filterIng = globalIngs.ingredientIngredientType.filter((ingIngType) =>
          isBufferIngredientType(ingIngType),
        );
        tempIng3 = filterIng.sort(compareWithStockPh);
        break;
      case ingredientTypesEnum.Prepared:
        const ingIds = globalIngs.ingredientStock
          .filter((stock) => stock.isPrepared)
          .map((s) => s.ingredientId)
          .filter(onlyUnique);
        tempIng3 = globalIngs.ingredientIngredientType
          .filter((ingIngType) => ingIds.includes(ingIngType.ingredientId))
          .sort(compareWithStockConcen);
        break;
      default:
        filterIng = globalIngs.ingredientIngredientType;
        tempIng3 = filterIng.slice().sort(compareWithIngredientName);
        break;
    }

    const tempIng: TSelectIngredient[] = tempIng3.map((ingingtype) => ({
      id: ingingtype.id,
      name: getIngNameByIngId(ingingtype.ingredientId),
      shortName: getIngShortNameByIngId(ingingtype.ingredientId),
    }));
    const tempIng2: TSelectIngredient[] = [
      ...new Map(tempIng.map((item) => [item['name'], item])).values(),
    ];
    setFilteredIngredients(
      tempIng2.filter(
        (ingIngType) =>
          ingIngType.name.toLowerCase().includes(searchIngredient.toLowerCase()) ||
          ingIngType.shortName.toLowerCase().includes(searchIngredient.toLowerCase()),
      ),
    );
  }

  function filterFavouriteIngredients() {
    let favStocks = globalFavIngStocks.length
      ? extractIngredientsByFavStockId(globalFavIngStocks)
      : extractIngredientsByFavStockId(GetFavouriteStocks());
    const globalIngs = GetIngredients();
    switch (selectedIngredientTypeFavorite) {
      case ingredientTypesEnum.NonBuffer:
        favStocks = favStocks.filter((stk) => {
          const ingIngType = globalIngs.ingredientIngredientType.find(
            (iit) => iit.ingredientId === stk.ingredient.id,
          );
          return ingIngType ? !isBufferIngredientType(ingIngType) : true;
        });
        break;
      case ingredientTypesEnum.Buffer:
        favStocks = favStocks.filter((stk) => {
          const ingIngType = globalIngs.ingredientIngredientType.find(
            (iit) => iit.ingredientId === stk.ingredient.id,
          );
          return ingIngType ? isBufferIngredientType(ingIngType) : false;
        });
        break;
      case ingredientTypesEnum.Prepared:
        favStocks = favStocks.filter((stk) => stk.stock.isPrepared);
        break;
      default:
        break;
    }
    if (searchFavStock !== '') {
      favStocks = favStocks.filter(
        (stk) =>
          stk.ingredient?.longName
            ?.toLowerCase()
            .includes(searchFavStock?.toLowerCase()) ||
          stk?.stock?.vendorName?.toLowerCase().includes(searchFavStock?.toLowerCase()),
      );
    }
    setFavouriteIngredientsList(favStocks);
  }

  function selectIngredient(value) {
    setSelectIngredient(value);
    setContentView(ELayersView.SELECTSTOCKSVIEW);
  }

  const selectFavouriteIngredientStock = (ingData) => {
    setSelectIngredient({
      id: GetIngredients().ingredientIngredientType.find(
        (iit) => iit.ingredientId === ingData.ingredient.id,
      )?.id,
      name: ingData.ingredient.longName,
      shortName: ingData.ingredient.shortName,
    });
    setSelectedStock(ingData.stock);
    setContentView(ELayersView.EDITINGREDIENTSVIEW);
  };

  function changeSearchIngredient(e) {
    setSearchIngredient(e.target.value);
  }

  function changeFavSearchStock(e) {
    setSearchFavStock(e.target.value);
  }

  return (
    <div id="selectingredients-wrapper" className="select-ingredients-tabs h-100">
      <Tabs id="selectingredients-tabs">
        <TabList
          id="selectingredients-tablist"
          style={{ display: 'flex', marginTop: '8px' }}
        >
          <Tab id="selectingredients-tab-all">All</Tab>
          <Tab id="selectingredients-tab-fav">Favorite</Tab>
        </TabList>
        <TabPanel id="selectingredients-tabpanel-all" style={{ height: '80%' }}>
          <div id="selectingredients-all-wrapper" className="d-flex flex-column">
            <SelectSearch
              selectValue={selectedIngredientTypeAll}
              setSelectValue={changeIngTypeAll}
              placeHolder="Search Ingredients"
              searchValue={searchIngredient}
              changeSearch={changeSearchIngredient}
            />
            <div id="selectingredients-all-list" className="ingredients-container">
              {filteredIngredients.map((ingredient, index) => (
                <IngredientItem
                  text={showIngLongName ? ingredient.name : ingredient.shortName}
                  key={index}
                  onClick={() => selectIngredient(ingredient)}
                />
              ))}
            </div>
          </div>
        </TabPanel>
        <TabPanel id="selectingredients-tabpanel-favorite">
          <div id="selectingredients-favorite-wrapper" className="d-flex flex-column">
            <SelectSearch
              selectValue={selectedIngredientTypeFavorite}
              setSelectValue={changeIngTypeFavorite}
              placeHolder="Search Favourite Stock Ingredients"
              searchValue={searchFavStock}
              changeSearch={changeFavSearchStock}
            />
            <div id="selectingredients-favorite-list" className="ingredients-container">
              {favouriteIngredientsList.map((ing, index) => (
                <FavouriteIngredientStockCard
                  ing={ing}
                  key={`favouriteIngStockCard-${index}`}
                  setGlobalFavIngStocks={setGlobalFavIngStocks}
                  onClick={() => selectFavouriteIngredientStock(ing)}
                />
              ))}
            </div>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
}

export default withTabBackButton(SelectIngredients);
